/* eslint-disable @typescript-eslint/consistent-type-assertions */

import type {
  DynamicTips,
  SweetpassUpsellCtaLocationInBag,
  TargetTippingByLocation,
} from './defaults.types';

/**
 * Don't forget, the current naming convention is in the format of:
 * PROJECT-NUMBER-short-feature-enabled
 * PROJECT-NUMBER-permanent-feature-enabled
 * e.g. "XP-1234-short-ticket-name-enabled"
 * @see {@link https://sweetgreen.atlassian.net/wiki/spaces/TD/pages/4528570450/Feature+Flags#%F0%9F%93%9B-Naming-Conventions Naming Conventions}
 *
 * We track feature flag deletions in this spreadsheet:
 * @see {@link https://docs.google.com/spreadsheets/d/1gm_97S3nVXb2ptnaGkg7uCHRnZmkSr1XSnqG77Yj4ew/edit#gid=0 Feature Flag Removal}
 * Please create a ticket for removing short term feature flags that you created and include it there.
 */

export const featureFlagDefaults = {
  'CELS-440-long-sweetpass-detailed-hero-stats-enabled': false,
  'permanent-forced-app-update-enabled': false,
  'permanent-order-crash-option-enabled': false,
  'permanent-contentful-preview-enabled': false,
  'permanent-apple-pay-enabled': false,

  // !! ───────────────────────────────────────────────────────────────────────
  // !!                           W A R N I N G                              !!
  // !! ───────────────────────────────────────────────────────────────────────
  // !! Most flags should be `true` by default.                              !!
  // !! Flag that are `false` by default are very difficult to remove later. !!
  // !! Please consider if your flag should be `false` before committing it. !!
  // !! It should be reserved for flags that should be `false` if deleted.   !!
  // !! Usually this means special features that aren't always turned on.    !!
  // !! ───────────────────────────────────────────────────────────────────────

  'permanent-scan-to-pay-enabled': true,
  'permanent-scan-to-pay-shake-listener-enabled': true,
  'permanent-in-app-review-enabled': true,
  'permanent-apple-wallet-enabled': true,
  'permanent-app-tracking-transparency-enabled': true,
  'permanent-refer-friend-enabled': true,
  'XP-3232-permanent-order-to-healthkit-enabled': true,

  'OPS-kustomer-enabled': true,
  'OPS-enable-maintenance-mode': false,
  'OPS-contentful-enabled': true,
  'OPS-ca-data-request': true,

  'MNU-656-deeplink-continue-button-enabled': true,
  'MNU-834-short-term-sum-calories-enabled': true,
  'MNU-1373-long-one-click-add-to-bag-enabled': true,
  'MNU-1373-long-one-click-add-to-bag-for-modifiable-products-enabled': true,
  'MNU-3430-short-s5-cyo-banner': true,

  'CELS-1191-permanent-dynamic-tip-values': {} as DynamicTips,
  'CELS-683-long-sweetpass-upsell-in-order-status-enabled': true,
  'CELS-683-long-sweetpass-upsell-in-bag-enabled': true,

  'CELS-953-short-homescreen-action-card-enabled': true,
  'CELS-308-short-new-challenges-completion-modal-enabled': true,
  'CELS-796-short-flash-challenge-card-theme-enabled': true,
  'CELS-683-short-sweetpass-upsell-cta-location-in-bag':
    'after-rewards' as SweetpassUpsellCtaLocationInBag,
  'CELS-1179-challenge-opt-in-v2-enabled': true,
  'CELS-1149-short-target-tipping-by-location-enabled': [
    'all',
  ] as TargetTippingByLocation,
  'CELS-1108-use-stripe-for-new-payment-methods-enabled': true,
  'CELS-1109-use-payment-svc-for-payment-method-operations': true,
  'CELS-1430-bottom-tabs-icons-enabled': true,
  'CELS-1410-enable-gift-card-reg-code': true,
  'CELS-1476-in-app-order-cancellation': true,
  'CELS-1558-subscription-credit-card-expiration-state': true,
  'CELS-1600-short-sweetpass-iso-date-enabled': true,
  'CELS-1260-enable-annual-subscriptions': true,
  'CELS-1700-in-store-rewards': true,
  'CELS-1902-short-default-payment-method-query-enabled': true,

  'em-716-personalized-bowl-ux': true,
  'em-888-hide-outpost-from-map-pickup-section': true,

  // ─── OTA Update ──────────────────────────────────────────────────────

  'OTA-update-in-app-checker-enabled': true,
  'OTA-update-in-app-checker-initial-check-timeout-ms': 10_000, // 10 seconds
  'OTA-update-in-app-checker-polling-interval-ms': 300_000, // 5 minutes
  'OTA-update-is-force-update-enabled': true,
};
